import debounce from 'lodash/debounce';

/**
 * Listen for a pure click event, which has no drag-and-drop action before.
 *
 * @param {EventTarget} target The target element
 * @param {function(Event)} listener Handler for a click event that has no
 * drag-and-drop action before
 * @returns {function()} The function to remove the event listeners just added
 */
function addClickWithoutDraggingEventListener(target, listener) {
  let drag = false;
  const setDrag = () => {
    drag = true;
  };
  const setNoDrag = () => {
    drag = false;
  };

  const handleClickOnly = (evt) => {
    if (!drag) {
      listener(evt);
    }
  };

  target.addEventListener('mousedown', setNoDrag);
  target.addEventListener('mousemove', setDrag);
  target.addEventListener('mouseup', handleClickOnly);

  return function removeClickWithoutDraggingEventListener() {
    target.removeEventListener('mousedown', setNoDrag);
    target.removeEventListener('mousemove', setDrag);
    target.removeEventListener('mouseup', handleClickOnly);
  };
}

/**
 * Attach a delegated event listener to an arbitrary node.
 * The handler is invoked when an event bubbles up from a descendant that
 * matches `elementSelector`, walking the DOM upward from the original target
 * until reaching the delegation node.
 *
 * @param {EventTarget} node - The node on which the listener is attached and
 *  at which bubbling stops (delegation root).
 * @param {string} eventName - The event type to listen for (e.g. `'click'`).
 * @param {string} elementSelector - CSS selector identifying the descendants
 *  that should trigger the handler.
 * @param {function(Event): void} handler - Callback invoked with the matched
 *  descendant as `this` and the original event as the first argument.
 */
const addDelegatedEventListener = (
  node,
  eventName,
  elementSelector,
  handler
) => {
  // eslint-disable-next-line listeners/no-missing-remove-event-listener, listeners/no-inline-function-event-listener
  node.addEventListener(
    eventName,
    (e) => {
      // loop parent nodes from the target to the delegation node
      for (
        let { target } = e;
        target && target !== node;
        target = target.parentNode
      ) {
        if (target.matches(elementSelector)) {
          handler.call(target, e);
          break;
        }
      }
    },
    false
  );
};

/**
 * Attach a delegated event listener to `document`.
 * Convenience wrapper around {@link addDelegatedListener} that uses `document`
 * as the delegation root.
 *
 * @param {string} eventName - The event type to listen for (e.g. `'click'`).
 * @param {string} elementSelector - CSS selector identifying the descendants
 *  that should trigger the handler.
 * @param {function(Event): void} handler - Callback invoked with the matched
 *  descendant as `this` and the original event as the first argument.
 */
const addDocumentDelegatedListener = (eventName, elementSelector, handler) => {
  addDelegatedEventListener(document, eventName, elementSelector, handler);
};

/**
 * Add a listener to the window resize event with a better performance
 *
 * @param {function(Event)} listener Handler for a debounced resize event
 * @param {Object} options Optional params
 * @param {number} options.debounceWait The number of milliseconds to delay
 * @returns {function()} The function to remove the just-added event listener
 */
function addWindowResizeListener(listener, { debounceWait = 200 } = {}) {
  const debouncedListener = debounce(listener, debounceWait);
  window.addEventListener('resize', debouncedListener, {
    passive: true,
  });

  return function removeWindowResizeListener() {
    window.removeEventListener('resize', debouncedListener);
  };
}

/**
 * Run a function when DOMContentLoaded event is fired, or run immediately if
 * the DOM is already loaded.
 * @param {Function} fn Callback function
 */
const onDOMContentLoaded = (fn) => {
  if (document.readyState === 'loading') {
    // Loading hasn't finished yet, add a listener with once option
    // eslint-disable-next-line listeners/no-missing-remove-event-listener
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    // `DOMContentLoaded` has already fired
    fn();
  }
};

/**
 * Add an event listener to a given element and remove it after first invocation
 *
 * @param {EventTarget} target DOM element for adding the event to
 * @param {string} eventType 'load', 'click'...
 * @param {Function} handlerFn Handler function for the event
 */
const onOnce = (target, eventType, handlerFn) => {
  // It does not need a remove event listener, it will be automatically removed
  // when invoked
  // eslint-disable-next-line listeners/no-missing-remove-event-listener
  target.addEventListener(eventType, handlerFn, { once: true });
};

export {
  addClickWithoutDraggingEventListener,
  addDelegatedEventListener,
  addDocumentDelegatedListener,
  addWindowResizeListener,
  onDOMContentLoaded,
  onOnce,
};
